import React from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CustomButton from "../CustomButton";
import CustomSelect from "../CustomSelect";
import CustomTextField from "../CustomTextField";
import CustomLabel from "../CustomLabel";
import { COLORS } from "../../assets/theme";
import { fetchProducts } from "../../services/ProductsNewService";
import { useFeatures } from "../../contexts/FeaturesContext";

export interface InvoiceItem {
  id: number;
  service: number | string;
  serviceId?: string;
  serviceLabel?: string;
  quantity: number;
  unitCost: number;
  discount: number;
  loyaltyDiscountPercent?: number;
  promoDiscountPercent?: number;
  discountManuallyEdited?: boolean;
  loyaltyRewardId?: string | number | null;
  loyaltyRewardType?: string | null;
  metadata?: Record<string, any>;
  totalPrice: number;
  itemType?: "SERVICE" | "PRODUCT";
}

export const isInvoiceProductMissingSelection = (item: any) => {
  if ((item?.itemType || "SERVICE") !== "PRODUCT") return false;

  const selectedValue = item?.serviceId ?? item?.service;
  return (
    selectedValue === undefined ||
    selectedValue === null ||
    String(selectedValue).trim() === ""
  );
};

interface InvoiceItemsEditorProps {
  value: InvoiceItem[] | undefined;
  onChange: (items: InvoiceItem[]) => void;
  vatValue?: number | string;
  onVatChange?: (value: number) => void;
  discountValue?: number | string;
  onDiscountChange?: (value: number) => void;
  promoDiscountValue?: number | string;
  onPromoDiscountChange?: (value: number) => void;
  promoValue?: number | string;
  promoType?: string;
  onPromoValueChange?: (value: number) => void;
  isEditMode?: boolean;
  servicesList?: any[];
  productsList?: any[];
}

type ServiceOption = {
  value: string | number;
  id?: string;
  name: string;
  price: number;
};

const clampPercentage = (value: number) => Math.min(100, Math.max(0, value));
const editablePercentValue = (value: number | string | undefined) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric === 0 ? "" : value ?? "";
};

const computeItemTotal = (
  item: Pick<InvoiceItem, "quantity" | "unitCost" | "discount">
) => {
  const qty = Number(item.quantity) || 0;
  const unit = Number(item.unitCost) || 0;
  const discountPercent = clampPercentage(Number(item.discount) || 0);
  const subtotal = qty * unit;
  const discountAmount = (subtotal * discountPercent) / 100;
  return Math.round((subtotal - discountAmount) * 100) / 100;
};

const DEFAULT_SERVICE_OPTIONS: ServiceOption[] = [
  { value: 60, name: "60", price: 20 },
  { value: 90, name: "90", price: 30 },
  { value: 120, name: "120", price: 40 },
];

const normalizeServiceOption = (service: any): ServiceOption | null => {
  if (!service) return null;

  const idCandidate =
    service?.id ?? service?._id ?? service?.serviceId ?? service?.service_id;
  const id =
    idCandidate === undefined || idCandidate === null
      ? undefined
      : String(idCandidate);

  const name = String(
    service?.name ??
      service?.title ??
      service?.service_name ??
      service?.label ??
      service?.description ??
      ""
  ).trim();

  const price = Number(
    service?.price ??
      service?.amount ??
      service?.unitPrice ??
      service?.unit_cost ??
      service?.serviceprice ??
      service?.servicePrice ??
      0
  );

  if (id) {
    return {
      value: id,
      id,
      name: name || id,
      price: Number.isFinite(price) ? price : 0,
    };
  }

  if (name) {
    return {
      value: name,
      name,
      price: Number.isFinite(price) ? price : 0,
    };
  }

  return null;
};

const normalizeProductOption = (product: any): ServiceOption | null => {
  if (!product) return null;

  const idCandidate =
    product?.id ?? product?._id ?? product?.productId ?? product?.product_id;
  const id =
    idCandidate === undefined || idCandidate === null
      ? undefined
      : String(idCandidate);

  const name = String(
    product?.name ??
      product?.title ??
      product?.label ??
      product?.description ??
      ""
  ).trim();

  const price = Number(
    product?.sale_price ??
      product?.salePrice ??
      product?.price ??
      product?.amount ??
      0
  );

  if (id) {
    return {
      value: id,
      id,
      name: name || id,
      price: Number.isFinite(price) ? price : 0,
    };
  }

  if (name) {
    return {
      value: name,
      name,
      price: Number.isFinite(price) ? price : 0,
    };
  }

  return null;
};

const InvoiceItemsEditor: React.FC<InvoiceItemsEditorProps> = ({
  value,
  onChange,
  vatValue = 0,
  onVatChange,
  discountValue = 0,
  onDiscountChange,
  promoValue = 0,
  promoType,
  onPromoValueChange,
  isEditMode = false,
  servicesList = [],
  productsList,
}) => {
  const { isEnabled } = useFeatures();
  // With STOCK off the API rejects any document carrying a product line, so the
  // picker is hidden rather than left to fail on save.
  const stockEnabled = isEnabled("STOCK");

  const ensureItemArray = (incoming: any): InvoiceItem[] =>
    Array.isArray(incoming)
      ? incoming.map((it: any) => ({
          ...it,
          itemType: it?.itemType || "SERVICE",
        }))
      : [];

  const [localItems, setLocalItems] = React.useState<InvoiceItem[]>(
    ensureItemArray(value)
  );

  const [localProducts, setLocalProducts] = React.useState<any[]>(
    Array.isArray(productsList) ? productsList : []
  );

  React.useEffect(() => {
    if (!stockEnabled) {
      setLocalProducts([]);
      return;
    }
    if (Array.isArray(productsList)) {
      setLocalProducts(productsList);
      return;
    }
    let isActive = true;
    (async () => {
      try {
        const data = await fetchProducts();
        if (!isActive) return;
        setLocalProducts(Array.isArray(data) ? data : []);
      } catch {
        if (!isActive) return;
        setLocalProducts([]);
      }
    })();
    return () => {
      isActive = false;
    };
  }, [productsList, stockEnabled]);

  const baseServiceOptions = React.useMemo<ServiceOption[]>(() => {
    const list = Array.isArray(servicesList) ? servicesList : [];
    const options = list
      .map((service) => normalizeServiceOption(service))
      .filter(Boolean) as ServiceOption[];

    if (options.length) return options;

    // If services were provided but none could be normalized, don't fall back.
    if (Array.isArray(servicesList) && servicesList.length > 0) return [];

    // Allow fallback only when no services are provided and not in edit mode.
    return isEditMode ? [] : DEFAULT_SERVICE_OPTIONS;
  }, [servicesList, isEditMode]);

  const serviceOptions = React.useMemo<ServiceOption[]>(() => {
    const options: ServiceOption[] = [...baseServiceOptions];
    const existing = new Set(options.map((opt) => String(opt.value)));

    localItems.forEach((it) => {
      if (it.itemType === "PRODUCT") return;
      const optionValue = (it.serviceId ?? it.service) as string | number;
      const key = String(optionValue ?? "");
      if (!key) return;
      if (existing.has(key)) return;
      existing.add(key);
      options.push({
        value: optionValue,
        id: it.serviceId,
        name: it.serviceLabel || key,
        price: Number(it.unitCost) || 0,
      });
    });

    return options;
  }, [baseServiceOptions, localItems]);

  const baseProductOptions = React.useMemo<ServiceOption[]>(() => {
    const list = Array.isArray(localProducts) ? localProducts : [];
    const options = list
      .map((product) => normalizeProductOption(product))
      .filter(Boolean) as ServiceOption[];

    return options;
  }, [localProducts]);

  const productOptions = React.useMemo<ServiceOption[]>(() => {
    return baseProductOptions;
  }, [baseProductOptions]);

  React.useLayoutEffect(() => {
    setLocalItems(ensureItemArray(value));
  }, [value]);

  const updateItems = (newItems: InvoiceItem[]) => {
    setLocalItems(newItems);
    onChange(newItems);
  };

  const handleAddItem = () => {
    const nextId = localItems.length
      ? Math.max(...localItems.map((it) => it.id)) + 1
      : 1;

    const firstOption =
      Array.isArray(baseServiceOptions) && baseServiceOptions.length > 0
        ? baseServiceOptions[0]
        : DEFAULT_SERVICE_OPTIONS[0];

    const newItem: InvoiceItem = {
      id: nextId,
      service: firstOption.value,
      serviceId:
        firstOption.id ||
        (typeof firstOption.value === "string" ? firstOption.value : undefined),
      serviceLabel: firstOption.name,
      quantity: 1,
      unitCost: Number(firstOption.price) || 0,
      discount: 0,
      totalPrice: computeItemTotal({
        quantity: 1,
        unitCost: Number(firstOption.price) || 0,
        discount: 0,
      }),
      itemType: "SERVICE",
    };

    updateItems([...localItems, newItem]);
  };

  const handleAddProduct = () => {
    const nextId = localItems.length
      ? Math.max(...localItems.map((it) => it.id)) + 1
      : 1;

    const newItem: InvoiceItem = {
      id: nextId,
      service: "",
      serviceId: undefined,
      serviceLabel: "",
      quantity: 1,
      unitCost: 0,
      discount: 0,
      totalPrice: 0,
      itemType: "PRODUCT",
    };

    updateItems([...localItems, newItem]);
  };

  const renderItemsTable = (
    items: InvoiceItem[],
    options: ServiceOption[],
    label: string,
    selectConfig?: {
      placeholder?: string;
      searchable?: boolean;
      searchPlaceholder?: string;
    }
  ) => {
    return (
      <TableContainer component={Paper} sx={{ mt: 1.5 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{label}</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>Unit Cost</TableCell>
              <TableCell>Discount (%)</TableCell>
              <TableCell>Total Price</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => {
              const itemValue = (item.serviceId ?? item.service) as
                | string
                | number;
              const hasItemValue = String(itemValue ?? "").trim() !== "";
              const hasOption = options.some(
                (option) => String(option.value) === String(itemValue)
              );
              const rowOptions =
                label === "Product" && hasItemValue && !hasOption
                  ? [
                      ...options,
                      {
                        value: itemValue,
                        id: item.serviceId,
                        name: item.serviceLabel || String(itemValue),
                        price: Number(item.unitCost) || 0,
                      },
                    ]
                  : options;
              const selectOptions = rowOptions.map((option) => ({
                label: `${option.name} - $${option.price}`,
                value: option.value,
              }));

              return (
                <TableRow key={item.id}>
                  <TableCell>
                    <CustomSelect
                      value={item.service ?? ""}
                      options={selectOptions}
                    onChange={(rawValue) => {
                      const rawSelectedValue = Array.isArray(rawValue)
                        ? rawValue[0] ?? ""
                        : rawValue;
                      const isEmptySelection =
                        rawSelectedValue === undefined ||
                        rawSelectedValue === null ||
                        String(rawSelectedValue).trim() === "";
                      const selectedOption = rowOptions.find(
                        (opt) => String(opt.value) === String(rawSelectedValue)
                      );

                      const nextValue = selectedOption
                        ? selectedOption.value
                        : rawSelectedValue;

                      const updatedItems = localItems.map((it) => {
                        if (it.id !== item.id) return it;
                        const updated: InvoiceItem = {
                          ...it,
                          service: isEmptySelection ? "" : nextValue,
                        };

                        if (isEmptySelection) {
                          updated.serviceId = undefined;
                          updated.serviceLabel = "";
                          updated.quantity = 1;
                          updated.unitCost = 0;
                          updated.discount = 0;
                          updated.discountManuallyEdited = false;
                        } else if (selectedOption) {
                          updated.serviceId =
                            selectedOption.id ||
                            (typeof selectedOption.value === "string"
                              ? selectedOption.value
                              : undefined);
                          updated.serviceLabel = selectedOption.name;
                          updated.quantity = 1;
                          updated.unitCost = selectedOption.price || 0;
                          updated.discount = 0;
                          updated.discountManuallyEdited = false;
                        }

                        updated.totalPrice = computeItemTotal(updated);
                        return updated;
                      });
                      updateItems(updatedItems);
                    }}
                    fullWidth
                    placeholder={
                      selectConfig?.placeholder ||
                      `Choose a ${label.toLowerCase()}`
                    }
                    renderPlaceholder
                    searchable={Boolean(selectConfig?.searchable)}
                    searchPlaceholder={
                      selectConfig?.searchPlaceholder ||
                      `Search ${label.toLowerCase()}s`
                    }
                    sx={{
                      minWidth: 190,
                      "& .MuiSelect-select": {
                        fontSize: "0.875rem",
                        py: "7px",
                      },
                    }}
                    />
                  </TableCell>
                <TableCell>
                  <CustomTextField
                    value={item.quantity}
                    type="number"
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      const updatedItems = localItems.map((it) => {
                        if (it.id !== item.id) return it;
                        const updated = {
                          ...it,
                          quantity: value,
                        } as InvoiceItem;
                        updated.totalPrice = computeItemTotal(updated);
                        return updated;
                      });
                      updateItems(updatedItems);
                    }}
                    minWidth="65px"
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <CustomTextField
                    value={item.unitCost}
                    type="number"
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      const updatedItems = localItems.map((it) => {
                        if (it.id !== item.id) return it;
                        const updated = {
                          ...it,
                          unitCost: value,
                        } as InvoiceItem;

                        const matchedOption = rowOptions.find(
                          (opt) => Number(opt.price) === value
                        );

                        if (matchedOption) {
                          updated.service = matchedOption.value;
                          updated.serviceId =
                            matchedOption.id ||
                            (typeof matchedOption.value === "string"
                              ? matchedOption.value
                              : undefined);
                          updated.serviceLabel = matchedOption.name;
                        }

                        updated.totalPrice = computeItemTotal(updated);
                        return updated;
                      });
                      updateItems(updatedItems);
                    }}
                    minWidth="65px"
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <CustomTextField
                    value={editablePercentValue(item.discount)}
                    type="number"
                    placeholder="0"
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      const updatedItems = localItems.map((it) => {
                        if (it.id !== item.id) return it;
                        const updated = {
                          ...it,
                          discount: clampPercentage(value),
                          discountManuallyEdited: true,
                        } as InvoiceItem;
                        updated.totalPrice = computeItemTotal(updated);
                        return updated;
                      });
                      updateItems(updatedItems);
                    }}
                    minWidth="65px"
                    size="small"
                    inputProps={{ step: 0.01, min: 0, max: 100 }}
                  />
                </TableCell>
                <TableCell>
                  <CustomTextField
                    value={item.totalPrice}
                    type="number"
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      const updatedItems = localItems.map((it) =>
                        it.id === item.id ? { ...it, totalPrice: value } : it
                      );
                      updateItems(updatedItems);
                    }}
                    minWidth="65px"
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Button
                    color="error"
                    size="small"
                    onClick={() => {
                      const updatedItems = localItems.filter(
                        (it) => it.id !== item.id
                      );
                      updateItems(updatedItems);
                    }}
                    sx={{ minWidth: 0, padding: 0 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </Button>
                </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const totalAmount = localItems.reduce(
    (sum, item) => sum + (Number(item.totalPrice) || 0),
    0
  );
  const discountPercent = Number(discountValue) || 0;
  const vatPercent = Number(vatValue) || 0;
  const promoValueAmount = Number(promoValue) || 0;
  const promoTypeLabel = String(promoType || "").trim() || "%";
  const discountAmount = (totalAmount * discountPercent) / 100;
  const amountAfterDiscount = totalAmount - discountAmount;
  const vatAmount = (amountAfterDiscount * vatPercent) / 100;
  const netAmount = Math.round((amountAfterDiscount + vatAmount) * 100) / 100;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <CustomLabel text="Services" fontWeight={600} />
        <CustomButton
          textColor={COLORS.WhiteText}
          label=""
          bgColor={COLORS.MainColor}
          iconButtonSize={40}
          startIcon={<AddIcon />}
          onClick={handleAddItem}
        />
      </Box>
      {renderItemsTable(
        localItems.filter((it) => it.itemType !== "PRODUCT"),
        serviceOptions,
        "Service"
      )}

      {stockEnabled && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 2,
              mb: 1,
            }}
          >
            <CustomLabel text="Products" fontWeight={600} />
            <CustomButton
              textColor={COLORS.WhiteText}
              label=""
              bgColor={COLORS.MainColor}
              iconButtonSize={40}
              startIcon={<AddIcon />}
              onClick={handleAddProduct}
            />
          </Box>
          {renderItemsTable(
            localItems.filter((it) => it.itemType === "PRODUCT"),
            productOptions,
            "Product",
            {
              placeholder: "Choose a product to add",
              searchable: true,
              searchPlaceholder: "Search products",
            }
          )}
        </>
      )}
      <Box sx={{ mt: 2 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            columnGap: 2,
            rowGap: 1.5,
            justifyItems: "stretch",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: { xs: "none", sm: "block" } }} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CustomLabel text="Subtotal" />
            <CustomLabel
              text={`${totalAmount.toFixed(2)}$`}
              fontWeight="bold"
            />
          </Box>

          {/* Row 2: Promo Value | Discount */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "left",
              gap: 2.4,
            }}
          >
            <CustomLabel text="Promo Value" />
            <CustomLabel text="" />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {isEditMode ? (
                <>
                  <CustomTextField
                    value={promoValueAmount}
                    type="number"
                    minWidth="80px"
                    onChange={(e) =>
                      onPromoValueChange?.(Number(e.target.value) || 0)
                    }
                    size="small"
                    disabled
                    inputProps={{ step: 0.01, min: 0, max: 100 }}
                  />
                  <CustomLabel text={promoTypeLabel} />
                </>
              ) : (
                <CustomLabel text={`${promoValueAmount} ${promoTypeLabel}`} />
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CustomLabel text="Discount" />
            <CustomLabel text="%" />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {isEditMode ? (
                <CustomTextField
                  value={editablePercentValue(discountPercent)}
                  type="number"
                  placeholder="0"
                  onChange={(e) =>
                    onDiscountChange?.(
                      clampPercentage(Number(e.target.value) || 0)
                    )
                  }
                  size="small"
                  sx={{ width: 80 }}
                  inputProps={{ step: 0.01, min: 0, max: 100 }}
                />
              ) : (
                <CustomLabel text={`${discountPercent}%`} />
              )}
            </Box>
          </Box>

          {/* Row 3: VAT */}
          {/* Row 3: (empty) | VAT (aligned under Discount) */}
          <Box />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CustomLabel text="VAT" />
            <CustomLabel text="%" />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {isEditMode ? (
                <CustomTextField
                  value={editablePercentValue(vatPercent)}
                  type="number"
                  placeholder="0"
                  onChange={(e) =>
                    onVatChange?.(clampPercentage(Number(e.target.value) || 0))
                  }
                  size="small"
                  sx={{ width: 80 }}
                  inputProps={{ step: 0.01, min: 0, max: 100 }}
                />
              ) : (
                <CustomLabel text={`${vatPercent}%`} />
              )}
            </Box>
          </Box>

          {/* Row 4: empty | Net Amount */}
          <Box sx={{ display: { xs: "none", sm: "block" } }} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderTop: "1px solid #e0e0e0",
              pt: 1,
              mt: 0.5,
            }}
          >
            <CustomLabel text="Net Amount" fontWeight="bold" />
            <CustomLabel text={`${netAmount.toFixed(2)}$`} fontWeight="bold" />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default InvoiceItemsEditor;
